import { useEffect, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { EMPLOYEES } from "@/constants/testIds";

/**
 * Leave tab — the full audit-friendly view Mike needs for spreadsheet
 * reconciliation. Shows opening (or "not supplied" flag), rolling accrued,
 * rolling taken, adjustments, and current balance for annual & sick.
 *
 * Each row in the timeline is traceable to either a pay period (via
 * pay_period_id + ppe_date) or an adjustment (with reason + who).
 *
 * Balances are NEVER floored — a negative balance is shown red so the
 * deficit is visible rather than hidden.
 */
export default function LeaveTabSection({ employeeId }) {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAdjust, setShowAdjust] = useState(null); // 'annual' | 'sick' | null

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/employees/${employeeId}/leave-summary`);
            setSummary(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to load leave summary");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (employeeId) load(); /* eslint-disable-next-line */ }, [employeeId]);

    if (!employeeId) return null;
    if (loading || !summary) {
        return <div className="text-[11px] text-neutral-400 py-4">Loading leave summary…</div>;
    }

    return (
        <div className="mb-8" data-testid="employee-drawer-leave-tab">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-3 border-b border-neutral-200 pb-2">
                Leave — accrual &amp; movements
            </div>

            <div className="grid grid-cols-2 gap-3">
                <LeaveCard
                    title="Annual leave"
                    tidPrefix="al"
                    data={summary.annual}
                    onAdjust={() => setShowAdjust("annual")}
                />
                <LeaveCard
                    title="Sick leave"
                    tidPrefix="sl"
                    data={summary.sick}
                    onAdjust={() => setShowAdjust("sick")}
                />
            </div>

            <MovementTimeline
                movements={summary.movements}
                adjustments={summary.adjustments}
            />

            {showAdjust && (
                <AdjustModal
                    employeeId={employeeId}
                    leaveType={showAdjust}
                    onClose={(saved) => {
                        setShowAdjust(null);
                        if (saved) load();
                    }}
                />
            )}
        </div>
    );
}

function fmt(v) { return Number(v || 0).toFixed(2); }
function fmtSigned(v) { const s = Number(v || 0); return (s >= 0 ? "+" : "") + s.toFixed(2); }

function LeaveCard({ title, tidPrefix, data, onAdjust }) {
    const balance = Number(data.balance || 0);
    const negative = balance < 0;
    return (
        <div className="border border-neutral-200 rounded-[3px] p-3 bg-white" data-testid={`leave-card-${tidPrefix}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-600 font-display">
                    {title}
                </div>
                <button
                    onClick={onAdjust}
                    data-testid={`leave-adjust-btn-${tidPrefix}`}
                    className="text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border border-neutral-300 rounded-[2px] text-neutral-600 hover:bg-neutral-50"
                >
                    Adjust
                </button>
            </div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mb-1 font-display">
                Balance
            </div>
            <div
                className={`text-3xl font-display font-bold tabular-nums ${negative ? "text-[color:var(--kp-red)]" : "text-[color:var(--kp-charcoal)]"}`}
                data-testid={`leave-balance-${tidPrefix}`}
            >
                {fmt(balance)}<span className="text-sm font-normal text-neutral-400 ml-1">h</span>
            </div>
            {data.from_cut_in_only && (
                <div className="inline-flex items-center gap-1 mt-2 text-[10px] tracking-[0.08em] px-2 py-0.5 rounded-[2px] bg-amber-50 border border-amber-300 text-amber-800"
                     data-testid={`leave-cut-in-flag-${tidPrefix}`}>
                    <Info size={11} /> From cut-in only — no opening figure supplied
                </div>
            )}
            {negative && (
                <div className="inline-flex items-center gap-1 mt-2 ml-2 text-[10px] tracking-[0.08em] px-2 py-0.5 rounded-[2px] bg-red-50 border border-red-300 text-red-800">
                    <AlertTriangle size={11} /> Negative — taken exceeds accrual + opening
                </div>
            )}
            <div className="mt-3 space-y-1 text-[11px] text-neutral-600 font-mono-money">
                <Line label="Opening" value={data.opening === null ? "— not supplied —" : fmt(data.opening)} muted={data.opening === null} />
                <Line label="Accrued to date" value={fmt(data.accrued)} />
                <Line label="Taken to date" value={fmt(data.taken)} negative />
                <Line label="Adjustments" value={fmtSigned(data.adjustments)} />
                <Line label="= Balance" value={fmt(data.balance)} bold negativeStyle={negative} />
            </div>
        </div>
    );
}

function Line({ label, value, negative, negativeStyle, bold, muted }) {
    return (
        <div className={`flex justify-between ${bold ? "border-t border-neutral-200 pt-1 mt-1 font-semibold" : ""}`}>
            <span className={muted ? "text-neutral-400 italic" : ""}>{label}</span>
            <span className={negativeStyle ? "text-[color:var(--kp-red)]" : ""}>
                {negative && Number(value) > 0 ? "−" : ""}{value}
            </span>
        </div>
    );
}

function MovementTimeline({ movements, adjustments }) {
    // Merge and sort: one row per pay period + one row per adjustment
    const items = [
        ...movements.map((m) => ({
            kind: "movement", key: `m-${m.id}`, sort: m.ppe_date || "",
            date: m.ppe_date, label: `Pay period · ${m.ppe_date}`,
            al_accrued: m.al_accrued, sl_accrued: m.sl_accrued,
            al_taken: m.al_taken, sl_taken: m.sl_taken,
            delta_al: m.delta_al, delta_sl: m.delta_sl,
            qh: m.qualifying_hours,
        })),
        ...adjustments.map((a) => ({
            kind: "adjustment", key: `a-${a.id}`, sort: a.date || "",
            date: a.date, label: a.reason,
            leave_type: a.leave_type, delta_hours: a.delta_hours,
            recorded_by: a.recorded_by,
        })),
    ].sort((a, b) => (b.sort || "").localeCompare(a.sort || ""));

    if (items.length === 0) {
        return (
            <div className="mt-6 text-[11px] text-neutral-400 italic border-l-2 border-neutral-200 pl-3 py-2">
                No leave movements or adjustments yet. Movements appear here when a pay period is approved.
            </div>
        );
    }

    return (
        <div className="mt-6" data-testid="leave-timeline">
            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2">
                Timeline — one line per pay period · adjustments
            </div>
            <div className="border border-neutral-200 rounded-[3px] bg-white overflow-hidden">
                <table className="w-full text-[11px] font-mono-money">
                    <thead className="text-[10px] uppercase tracking-[0.12em] text-neutral-500 bg-neutral-50">
                        <tr>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">Description</th>
                            <th className="text-right px-3 py-2">QH</th>
                            <th className="text-right px-3 py-2">AL Δ</th>
                            <th className="text-right px-3 py-2">SL Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((it) => (
                            <tr key={it.key} className="border-t border-neutral-100">
                                <td className="px-3 py-2 text-neutral-500">{it.date}</td>
                                <td className="px-3 py-2">
                                    {it.kind === "movement" ? (
                                        <span>Pay period approved</span>
                                    ) : (
                                        <span>
                                            <span className="text-[10px] uppercase tracking-[0.1em] text-neutral-500 mr-1">{it.leave_type}</span>
                                            {it.label}
                                            {it.recorded_by && <span className="text-neutral-400"> — {it.recorded_by}</span>}
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right text-neutral-500">
                                    {it.kind === "movement" ? fmt(it.qh) : "—"}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {it.kind === "movement"
                                        ? <Delta value={it.delta_al} />
                                        : (it.leave_type === "annual" ? <Delta value={it.delta_hours} /> : "—")}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {it.kind === "movement"
                                        ? <Delta value={it.delta_sl} />
                                        : (it.leave_type === "sick" ? <Delta value={it.delta_hours} /> : "—")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Delta({ value }) {
    const v = Number(value || 0);
    if (v === 0) return <span className="text-neutral-400">0.00</span>;
    return <span className={v < 0 ? "text-[color:var(--kp-red)]" : "text-emerald-700"}>{fmtSigned(v)}</span>;
}

function AdjustModal({ employeeId, leaveType, onClose }) {
    const [delta, setDelta] = useState(0);
    const [reason, setReason] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!reason.trim()) return toast.error("Reason is required");
        setSaving(true);
        try {
            await api.post(`/employees/${employeeId}/leave-adjustment`, {
                date, leave_type: leaveType,
                delta_hours: Number(delta) || 0,
                reason: reason.trim(),
            });
            toast.success("Adjustment recorded");
            onClose(true);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => onClose(false)}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                  className="bg-white w-[460px] max-w-[95vw] rounded-[3px] p-6 shadow-xl">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-display mb-2">
                    Adjust {leaveType === "annual" ? "annual" : "sick"} leave balance
                </div>
                <div className="text-[11px] text-neutral-600 mb-4">
                    Signed delta in hours — positive tops up, negative reduces. Every adjustment
                    goes to the audit trail with your reason.
                </div>
                <label className="block mb-3">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">Date</span>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                           className="mt-1 w-full border border-neutral-300 rounded-[2px] px-2 py-1 text-sm" required />
                </label>
                <label className="block mb-3">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">Delta (hours, signed)</span>
                    <input type="number" step="0.01" value={delta} onChange={(e) => setDelta(e.target.value)}
                           data-testid="leave-adjust-delta"
                           className="mt-1 w-full border border-neutral-300 rounded-[2px] px-2 py-1 text-sm font-mono-money" required />
                </label>
                <label className="block mb-4">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">Reason</span>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                              data-testid="leave-adjust-reason"
                              className="mt-1 w-full border border-neutral-300 rounded-[2px] px-2 py-1 text-sm" required
                              placeholder="e.g. Correcting bookkeeper's spreadsheet entry from 2025 Q4" />
                </label>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => onClose(false)}
                            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] border border-neutral-300 rounded-[2px]">
                        Cancel
                    </button>
                    <button type="submit" disabled={saving} data-testid="leave-adjust-submit"
                            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] text-white rounded-[2px] disabled:opacity-60"
                            style={{ background: "var(--kp-red)" }}>
                        {saving ? "Saving…" : "Record adjustment"}
                    </button>
                </div>
            </form>
        </div>
    );
}
